import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Sparkles, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Ricerca() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [semRes, setSemRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [semLoading, setSemLoading] = useState(false);
  const navigate = useNavigate();

  const go = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setRes(data); }
    finally { setLoading(false); }
  };

  const goSemantic = async () => {
    if (!q.trim()) return;
    setSemLoading(true);
    try { const { data } = await api.post(`/search/semantic`, { q }); setSemRes(data); }
    finally { setSemLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-heading font-bold text-navy-900">Ricerca</h1>
        <p className="text-sm text-slate-500 mt-1">Ricerca classica per parole chiave o semantica con AI</p>
      </div>

      <Card className="p-5">
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input className="pl-9 h-11" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&go()} placeholder='Es. "contratto Rossi" oppure "pratiche con scadenze in ritardo"' data-testid="search-input"/>
          </div>
          <Button onClick={go} className="bg-navy-800 hover:bg-navy-900 h-11 px-6" data-testid="search-btn">Cerca</Button>
          <Button onClick={goSemantic} disabled={semLoading} className="bg-amber-500 hover:bg-amber-400 text-navy-900 h-11 px-6" data-testid="semantic-btn">
            {semLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" />Semantica AI</>}
          </Button>
        </div>
      </Card>

      <Tabs defaultValue="classic">
        <TabsList>
          <TabsTrigger value="classic">Risultati classici</TabsTrigger>
          <TabsTrigger value="semantic">Semantica AI</TabsTrigger>
        </TabsList>
        <TabsContent value="classic">
          {loading && <div className="text-sm text-slate-500">Ricerca in corso...</div>}
          {res && (
            <div className="grid lg:grid-cols-2 gap-5">
              <Card className="p-5">
                <div className="font-heading font-semibold text-navy-900 mb-3">Pratiche ({res.practices.length})</div>
                <div className="space-y-2">
                  {res.practices.map(p => (
                    <button key={p.id} onClick={()=>navigate(`/pratiche/${p.id}`)} className="block w-full text-left p-3 border border-slate-100 rounded hover:bg-slate-50">
                      <div className="font-mono text-xs text-slate-500">{p.numero_protocollo}</div>
                      <div className="text-sm font-medium text-navy-900">{p.title}</div>
                    </button>
                  ))}
                  {res.practices.length === 0 && <div className="text-xs text-slate-400">Nessun risultato</div>}
                </div>
              </Card>
              <Card className="p-5">
                <div className="font-heading font-semibold text-navy-900 mb-3">Contatti ({res.contacts.length})</div>
                <div className="space-y-2">
                  {res.contacts.map(c => (
                    <div key={c.id} className="p-3 border border-slate-100 rounded">
                      <div className="text-sm font-medium text-navy-900">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.email}</div>
                    </div>
                  ))}
                  {res.contacts.length === 0 && <div className="text-xs text-slate-400">Nessun risultato</div>}
                </div>
              </Card>
              <Card className="p-5">
                <div className="font-heading font-semibold text-navy-900 mb-3">Documenti ({res.documents.length})</div>
                <div className="space-y-2">
                  {res.documents.map(d => (
                    <div key={d.id} className="p-3 border border-slate-100 rounded">
                      <div className="text-sm font-medium text-navy-900">{d.name}</div>
                      <Badge variant="outline" className="text-xs mt-1">{d.doc_type}</Badge>
                    </div>
                  ))}
                  {res.documents.length === 0 && <div className="text-xs text-slate-400">Nessun risultato</div>}
                </div>
              </Card>
              <Card className="p-5">
                <div className="font-heading font-semibold text-navy-900 mb-3">Note ({res.notes.length})</div>
                <div className="space-y-2">
                  {res.notes.map(n => (
                    <div key={n.id} className="p-3 border border-slate-100 rounded text-sm text-slate-700">{n.content.slice(0,150)}</div>
                  ))}
                  {res.notes.length === 0 && <div className="text-xs text-slate-400">Nessun risultato</div>}
                </div>
              </Card>
            </div>
          )}
        </TabsContent>
        <TabsContent value="semantic">
          {semLoading && <div className="text-sm text-slate-500">Claude sta analizzando il catalogo...</div>}
          {semRes && (
            <Card className="p-5">
              <div className="font-heading font-semibold text-navy-900 mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Top risultati semantici ({semRes.results?.length || 0})</div>
              <div className="space-y-2">
                {(semRes.results || []).map((r, i) => (
                  <div key={i} className="p-3 border border-slate-100 rounded hover:bg-slate-50 cursor-pointer" onClick={()=>r.type === "practice" && navigate(`/pratiche/${r.id}`)} data-testid={`sem-result-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] capitalize">{r.type}</Badge>
                      {r.numero_protocollo && <span className="font-mono text-xs text-slate-500">{r.numero_protocollo}</span>}
                      <span className="text-sm font-medium text-navy-900">{r.title || r.name || (r.content || "").slice(0,80)}</span>
                    </div>
                    {r.description && <div className="text-xs text-slate-600 mt-1">{r.description.slice(0,200)}</div>}
                  </div>
                ))}
                {(!semRes.results || semRes.results.length === 0) && <div className="text-xs text-slate-400 text-center py-8">Nessun risultato semantico</div>}
              </div>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
